// ─── useRecentChanges — load + index recent audit rows by fieldKey ──────────
// Shared by AuditLogTab and every <FieldChangeMarker/> on every page, so we
// fetch once per session (plus occasional refresh) and all consumers read
// from the same in-memory Map.
//
// Provides:
//   • useRecentChanges(session)  → { byFieldKey, refresh, loading, error }
//   • The byFieldKey Map is keyed by details.fieldKey, and the value is the
//     most recent audit row for that key (server returns rows newest-first).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./useData.js";

// Module-level cache so multiple hook instances share results. Refreshed
// opportunistically; cache key is the access token so switching users busts it.
let _cache = { token: null, rows: null, fetchedAt: 0 };
const REFRESH_MS = 60_000; // one minute — good enough for "2h ago" UX

export function useRecentChanges() {
  const [rows, setRows] = useState(() => _cache.rows || []);
  const [loading, setLoading] = useState(!_cache.rows);
  const [error, setError] = useState("");
  const inflight = useRef(false);

  const refresh = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) { setLoading(false); return; }
      const res = await fetch("/api/account", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
        body: JSON.stringify({ action: "audit_log", limit: 300 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "failed");
      _cache = { token: s.access_token, rows: data.events || [], fetchedAt: Date.now() };
      setRows(_cache.rows);
      setError("");
    } catch (e) {
      setError(e?.message || "failed");
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, []);

  useEffect(() => {
    // Fresh fetch if cache is stale
    const stale = !_cache.rows || (Date.now() - _cache.fetchedAt > REFRESH_MS);
    if (stale) refresh();
    // Auto-refresh on window focus — cheap since cache-level
    function onFocus() {
      if (Date.now() - _cache.fetchedAt > REFRESH_MS) refresh();
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Index: fieldKey → most recent row (first occurrence in rows since rows
  // come newest-first from the server).
  const byFieldKey = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r?.details?.fieldKey;
      if (!key) continue;
      if (!map.has(key)) map.set(key, r);
    }
    return map;
  }, [rows]);

  return { rows, byFieldKey, refresh, loading, error };
}
